/**
 * AB#21824 — Create with ELSA: the order-drafting chat.
 *
 * Stateless backend, like Create with Atlas: the SPA keeps the transcript, the
 * last validated draft and its own upload session, and sends them back each
 * turn. The backend — not the model — resolves every name to a record of the
 * chosen merchant and decides whether the draft is ready. Nothing here creates
 * an order: `review` hands the draft to the existing Create manual order form,
 * and a person presses Create there, through the same endpoint and safeguards.
 */
import { computed, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { http, handleResponseException } from '@/interceptors/axiosRequestInterceptor';

export interface ElsaOrderFeatures {
  create_order_with_elsa?: boolean;
}

/** Backend flag only; the caller also checks can('manual-create-order') for the manual path. */
export const elsaOrderAllowed = (features?: ElsaOrderFeatures | null): boolean =>
  features?.create_order_with_elsa === true;

export interface ElsaOrderMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface DraftRosterRow {
  size: string;
  quantity: number;
  name: string;
  number: string;
}

export interface DraftProduct {
  product: {
    id: number;
    sku_id: string;
    sku_number: string;
    factory_id: number | null;
    factory_name: string;
  } | null;
  is_custom_product: boolean;
  new_product_name: string;
  style: { id: number; name: string } | null;
  addons: { id: number; title: string }[];
  design_nick_name: string;
  design_nickname_not_applicable: boolean;
  design_id: string;
  roster: DraftRosterRow[];
}

export type DraftFileType = 'assets' | 'front_image' | 'back_image' | 'po_document';

export interface DraftFile {
  type: DraftFileType;
  product_index: number;
  path: string;
  name: string;
  original_name: string;
  extension: string;
  size: number;
}

export interface DraftCustomAddress {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  company_name: string;
  address1: string;
  address2: string;
  city: string;
  zip_code: string;
  state: string;
  country: { id: number; name: string } | null;
  complete: boolean;
}

export interface OrderDraft {
  merchant: { id: number; name: string } | null;
  customer: { id: number; name: string; email: string } | null;
  address: { id: number; label: string } | null;
  custom_address: DraftCustomAddress | null;
  order_type: { symbol: string; label: string };
  customer_reference_no: string;
  po_number: string;
  general_comments: string;
  products: DraftProduct[];
  files: DraftFile[];
  upload_uuid: string;
}

export interface ChecklistItem {
  key: string;
  label: string;
  done: boolean;
}

export interface OrderTurn {
  reply: string | null;
  draft: OrderDraft;
  checklist: ChecklistItem[];
  problems: string[];
  dropped: string[];
  questions: string[];
  confirm: string[];
  ready: boolean;
  error?: string;
}

/** Files that never leave the browser until the form posts them: the PO document and roster sheets. */
export interface LocalDraftFiles {
  po_document: File | null;
  rosters: Record<number, File>;
}

/** What "Review & create" hands to the Create manual order form. */
export interface ElsaOrderPrefill {
  draft: OrderDraft;
  local: LocalDraftFiles;
}

/** Turns sent per request; matches the backend's history window. */
export const MAX_SENT_MESSAGES = 24;

export const OPENING =
  'Describe the order — the merchant, the customer, what ships where, and each product with its sizes and quantities. I only use merchants, customers and products from the catalogue, and I never place the order: you review it on the form and press Create.';

export const emptyDraft = (): OrderDraft => ({
  merchant: null,
  customer: null,
  address: null,
  custom_address: null,
  order_type: { symbol: 'Oi', label: 'Order with invoice' },
  customer_reference_no: '',
  po_number: '',
  general_comments: '',
  products: [],
  files: [],
  upload_uuid: uuidv4(),
});

const initialChecklist = (): ChecklistItem[] => [
  { key: 'merchant', label: 'Merchant', done: false },
  { key: 'customer', label: 'Customer', done: false },
  { key: 'address', label: 'Shipping address', done: false },
  { key: 'order_type', label: 'Order type', done: true },
  { key: 'products', label: 'Products', done: false },
  { key: 'quantities', label: 'Sizes and quantities', done: false },
];

export const useOrderAssistant = () => {
  const messages = ref<ElsaOrderMessage[]>([{ role: 'assistant', content: OPENING }]);
  const draft = ref<OrderDraft>(emptyDraft());
  const checklist = ref<ChecklistItem[]>(initialChecklist());
  const problems = ref<string[]>([]);
  const dropped = ref<string[]>([]);
  const questions = ref<string[]>([]);
  const confirm = ref<string[]>([]);
  /** Custom products the person has ticked — every one must be, to review. */
  const confirmed = ref<Set<string>>(new Set());
  const ready = ref(false);
  const isThinking = ref(false);
  const isUploading = ref(false);
  const error = ref<string | null>(null);
  const local = ref<LocalDraftFiles>({ po_document: null, rosters: {} });
  /** Roster rows parsed from a sheet, re-applied to the line after every turn. */
  const parsedRosters = ref<Record<number, DraftRosterRow[]>>({});

  const applyRosters = () => {
    for (const [idx, rows] of Object.entries(parsedRosters.value)) {
      const line = draft.value.products[Number(idx)];
      if (line && line.roster.length === 0) line.roster = rows;
    }
  };

  const apply = (turn: OrderTurn) => {
    // The upload session and files are ours; the backend only echoes what it accepted.
    const uuid = draft.value.upload_uuid;
    draft.value = { ...turn.draft, upload_uuid: turn.draft.upload_uuid || uuid };
    applyRosters();
    checklist.value = turn.checklist ?? checklist.value;
    problems.value = turn.problems ?? [];
    dropped.value = turn.dropped ?? [];
    questions.value = turn.questions ?? [];
    confirm.value = turn.confirm ?? [];
    confirmed.value = new Set([...confirmed.value].filter((n) => confirm.value.includes(n)));
    ready.value = turn.ready === true;
  };

  const send = async (text: string): Promise<boolean> => {
    const content = text.trim();
    if (!content || isThinking.value) return false;
    messages.value.push({ role: 'user', content });
    isThinking.value = true;
    error.value = null;
    try {
      // The backend reads the last turns only (the draft carries the state), so a
      // long conversation never grows past what it accepts.
      const outgoing = messages.value
        .filter((m, i) => !(i === 0 && m.role === 'assistant'))
        .slice(-MAX_SENT_MESSAGES);
      const { data } = await http.post('orders/assistant/turn', {
        messages: outgoing,
        draft: draft.value,
      });
      const turn = (data?.result ?? {}) as OrderTurn;
      if (data?.success === false || turn.error) {
        error.value = turn.error || data?.message || 'ELSA could not answer just now.';
        if (turn.draft) apply(turn);
        return false;
      }
      apply(turn);
      if (turn.reply) messages.value.push({ role: 'assistant', content: turn.reply });
      return true;
    } catch (e) {
      handleResponseException(e);
      error.value = 'ELSA could not answer just now.';
      return false;
    } finally {
      isThinking.value = false;
    }
  };

  /**
   * Artwork and images go to the same temp folder the manual form uses, under
   * this draft's own upload session, so the form's Create moves them with the
   * order and nothing is uploaded twice. The PO document and roster sheets stay
   * in the browser: the form posts the PO with the order and parses the sheet.
   */
  const addFiles = async (
    type: DraftFileType | 'roster',
    productIndex: number,
    files: File[],
    parseRoster?: (file: File) => Promise<DraftRosterRow[]>
  ): Promise<void> => {
    if (type === 'po_document') {
      local.value.po_document = files[0] ?? null;
      return;
    }
    if (type === 'roster') {
      const file = files[0];
      if (!file) return;
      local.value.rosters = { ...local.value.rosters, [productIndex]: file };
      if (parseRoster) {
        try {
          parsedRosters.value = { ...parsedRosters.value, [productIndex]: await parseRoster(file) };
          applyRosters();
        } catch {
          error.value = `${file.name} could not be read as a roster sheet.`;
        }
      }
      return;
    }
    isUploading.value = true;
    try {
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        form.append('uuid', draft.value.upload_uuid);
        form.append('type', type);
        const { data } = await http.post('/manual-order-assets', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const f = data?.file;
        if (!f?.path) continue;
        const entry: DraftFile = {
          type,
          product_index: productIndex,
          path: f.path,
          name: f.name,
          original_name: f.original_name ?? file.name,
          extension: f.extension ?? '',
          size: Math.round((f.file_size ?? file.size / (1024 * 1024)) * 1024 * 1024),
        };
        // One front and one back image per line; any number of assets.
        draft.value.files = [
          ...draft.value.files.filter(
            (x) => type === 'assets' || !(x.type === type && x.product_index === productIndex)
          ),
          entry,
        ];
      }
    } catch (e) {
      handleResponseException(e);
      error.value = 'A file could not be uploaded.';
    } finally {
      isUploading.value = false;
    }
  };

  const removeFile = (path: string) => {
    draft.value.files = draft.value.files.filter((f) => f.path !== path);
  };

  const toggleConfirm = (name: string) => {
    const next = new Set(confirmed.value);
    next.has(name) ? next.delete(name) : next.add(name);
    confirmed.value = next;
  };

  const unconfirmed = computed(() => confirm.value.filter((n) => !confirmed.value.has(n)));

  /** Ready on the backend's word AND every custom product ticked. */
  const canReview = computed(() => ready.value && unconfirmed.value.length === 0);

  const prefill = computed<ElsaOrderPrefill>(() => ({ draft: draft.value, local: local.value }));

  const reset = () => {
    messages.value = [{ role: 'assistant', content: OPENING }];
    draft.value = emptyDraft();
    checklist.value = initialChecklist();
    problems.value = [];
    dropped.value = [];
    questions.value = [];
    confirm.value = [];
    confirmed.value = new Set();
    ready.value = false;
    error.value = null;
    local.value = { po_document: null, rosters: {} };
    parsedRosters.value = {};
  };

  return {
    messages,
    draft,
    checklist,
    problems,
    dropped,
    questions,
    confirm,
    confirmed,
    unconfirmed,
    ready,
    canReview,
    isThinking,
    isUploading,
    error,
    local,
    prefill,
    send,
    addFiles,
    removeFile,
    toggleConfirm,
    reset,
  };
};
